import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Google Analytics 4 wiring.
 *
 * The gtag.js library and the initial config call live in public/index.html
 * — loaded synchronously in <head> so they're ready before React mounts.
 * That setup uses the canonical `function gtag(){dataLayer.push(arguments)}`
 * pattern that gtag.js expects.
 *
 * This component fires a page_view on every react-router location change
 * (the initial config in index.html sets `send_page_view: false` so we
 * control page views ourselves and get accurate page_title for SPA routes).
 *
 * IMPORTANT: To avoid double-counting, in GA4 → Admin → Data streams →
 * web stream → Enhanced Measurement (gear icon), toggle OFF
 * "Page changes based on browser history events".
 */

const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

/**
 * Routes we treat as case studies for engagement tracking. After 5s on
 * one of these routes, fire `case_study_viewed`. That filters out bounces
 * (people who clicked through and immediately left) so the metric reflects
 * intent to actually read the work.
 */
const CASE_STUDY_PATHS = ['/schrodinger', '/ballys', '/rrms', '/jerry', '/ourdate', '/multi-box'];

/**
 * Both lookups below key off the pathname exactly, and two different
 * pathnames reach the same route:
 *
 *   clicking a card   -> client-side nav to "/ballys"
 *   a shared link     -> CloudFront 302s "/ballys" to "/ballys/"
 *
 * So every direct or shared visit used to miss both tables: the title
 * fell back to the homepage's, and case_study_viewed never fired at all.
 * That undercounted exactly the traffic that matters most for a link
 * someone sends to a recruiter.
 */
const normalize = (pathname) => pathname.replace(/\/+$/, '') || '/';

/**
 * Per-route document titles. Used both as the <title> of the page (so
 * browser tabs and OS task switchers show something meaningful) and as
 * the `page_title` parameter on every page_view event — that's the
 * primary dimension GA4's Pages and screens report sorts by.
 *
 * Falls back to ROUTE_TITLES['/'] for any unmapped route.
 */
const ROUTE_TITLES = {
  '/': 'Chidrupa Mamunooru — Product Builder',
  '/about': 'About — Chidrupa Mamunooru',
  '/schrodinger': 'Schrödinger — Chidrupa Mamunooru',
  '/ballys': "Bally's — Chidrupa Mamunooru",
  '/rrms': 'RRMS — Chidrupa Mamunooru',
  '/jerry': 'Jerry — Chidrupa Mamunooru',
  '/ourdate': 'OurDate — Chidrupa Mamunooru',
  '/multi-box': 'Redesigning the supply chain for multi-box products — Chidrupa Mamunooru',
};

/**
 * Fire a custom GA4 event. No-op if gtag isn't loaded (dev builds without
 * the measurement ID, or while the script is still downloading), and
 * no-op when this page is loaded inside an iframe — most commonly the
 * splash chooser's live previews. Otherwise the splash would double-count
 * page_view for every visitor.
 */
export function trackEvent(name, params = {}) {
  if (!GA_ID || typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  if (window.self !== window.top) return;
  window.gtag('event', name, params);
}

export default function RouteAnalytics() {
  const location = useLocation();

  // Update document.title BEFORE firing page_view, so the title that lands
  // in GA reflects the current route — not whatever the previous route set.
  useEffect(() => {
    const title = ROUTE_TITLES[normalize(location.pathname)] || ROUTE_TITLES['/'];
    document.title = title;
    trackEvent('page_view', {
      page_path: normalize(location.pathname) + location.search,
      page_title: title,
      page_location: window.location.href,
    });
  }, [location]);

  // case_study_viewed — fires 5s after entering a case study route, only
  // if the user is still there. Cleared on route change or unmount.
  useEffect(() => {
    if (!CASE_STUDY_PATHS.includes(normalize(location.pathname))) return undefined;
    const timer = setTimeout(() => {
      trackEvent('case_study_viewed', {
        page_path: normalize(location.pathname),
        page_title: document.title,
      });
    }, 5000);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  return null;
}
